import { useEffect } from "react";

/**
 * Client-side SEO manager. Sets title + meta description + canonical +
 * Open Graph tags + optional JSON-LD Article schema. Cleans up on unmount so
 * the next page doesn't inherit stale tags.
 */
export default function usePostSeo({
  title,
  description,
  canonical,
  ogType = "website",
  article = null, // { title, author, publish_date, url }
}) {
  useEffect(() => {
    if (!title) return;
    document.title = title;

    const upsertMeta = (attr, key, content) => {
      if (!content) return null;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };

    const created = [];

    created.push(upsertMeta("name", "description", description));
    created.push(upsertMeta("property", "og:title", title));
    created.push(upsertMeta("property", "og:description", description));
    created.push(upsertMeta("property", "og:type", ogType));
    if (canonical) created.push(upsertMeta("property", "og:url", canonical));

    // Canonical link
    let canonEl = document.head.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!canonEl) {
        canonEl = document.createElement("link");
        canonEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonEl);
      }
      canonEl.setAttribute("href", canonical);
    }

    // JSON-LD Article schema
    let ldEl = null;
    if (article) {
      ldEl = document.createElement("script");
      ldEl.type = "application/ld+json";
      ldEl.setAttribute("data-blog-jsonld", "1");
      ldEl.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        author: { "@type": "Organization", name: article.author || "Regulens Team" },
        datePublished: article.publish_date,
        mainEntityOfPage: article.url,
        publisher: {
          "@type": "Organization",
          name: "Regulens",
        },
      });
      document.head.appendChild(ldEl);
    }

    return () => {
      if (ldEl && ldEl.parentNode) ldEl.parentNode.removeChild(ldEl);
    };
  }, [title, description, canonical, ogType, article]);
}
